import { Head, router, usePage } from '@inertiajs/react'
import type { SharedPageProps } from '@/types'
import { isStateDisabled } from './stateTransitions'
import { useState, useCallback, useRef, useEffect } from 'react'
import AppLayout from '@/layouts/AppLayout'
import Notification from '@/components/ui/Notification'
import FileUpload from '@/components/ui/FileUpload'
import { Badge } from '@/components/catalyst/badge'
import { Fieldset, Legend, FieldGroup, Field as CatalystField, Label } from '@/components/catalyst/fieldset'
import { FormSelect, FormInput, FormCheckbox } from '@/components/ui/form'
import { Dropdown, DropdownButton, DropdownMenu, DropdownItem as CatalystDropdownItem, DropdownLabel, DropdownDivider, DropdownHeading, DropdownSection } from '@/components/catalyst/dropdown'
import { XMarkIcon } from '@heroicons/react/16/solid'
import { ArrowPathIcon, DocumentTextIcon, CubeIcon, ClipboardDocumentListIcon, ListBulletIcon, TrashIcon, ClipboardDocumentCheckIcon, TruckIcon, BanknotesIcon, ArchiveBoxIcon, DocumentDuplicateIcon, ShoppingCartIcon } from '@heroicons/react/24/outline'
import { ClipboardDocumentListIcon as ClipboardDocumentListSolid, TruckIcon as TruckSolid, BanknotesIcon as BanknotesSolid, CubeIcon as CubeSolid, DocumentTextIcon as DocumentTextSolid, ClockIcon as ClockSolid, ChatBubbleLeftEllipsisIcon as ChatSolid } from '@heroicons/react/20/solid'
import { useOrderStatus } from '@/hooks/useOrderStatus'
import { usePermissions } from '@/hooks/usePermissions'
import ProductsTab from './ProductsTab'
import ShipmentTab from './ShipmentTab'
import FinanceTab from './FinanceTab'
import SummaryTab from './SummaryTab'
import StatusLogTab from './StatusLogTab'
import Avatar from '@/components/ui/Avatar'
import clsx from 'clsx'

// ─── Types ───────────────────────────────────────────────────

interface Order {
  id: number
  po_number: string
  state: string
  // Raw integer index into `sub_state_names[state]` / `sub_state_comments[state]`.
  // The backend now returns the integer (not a label), so the dropdown can
  // round-trip a value through PATCH without lossy string→int conversion.
  sub_state_name: number | null
  sub_state_comment: number | null
  sub_id: number | null
  probability: string
  finance_stage: string
  new_season: string
  currency: string
  ship_point: string
  order_type: string
  approved: boolean
  test_order: boolean
  company_id: number | null
  customer_id: number | null
  vendor_id: number | null
  user_id: number | null
  manager_id: number | null
  brand_partner_id: number | null
  brand_ids: number[]
  customer_po: string | null
  vendor_po: string | null
  offer: string | null
  brand_owner: string | null
  ghost_id: number | null
  ghost_platform_link: string | null
  customer_payment_term_id: number | null
  units: number
  customer_freight_cost: number
  customer_additional_cost: number
  customer_invoice_amount: number
  amount: number
  order_date: string | null
  vendor_order_date: string | null
  vendor_start_ship_date: string | null
  vendor_cancel_date: string | null
  po_date: string | null
  po_start_shipping: string | null
  po_cancel_date: string | null
  customer_name: string | null
  vendor_name: string | null
  company_name: string | null
  user_name: string | null
  brand_names: string[]
  state_updated_at: string | null
  created_at: string
  tag_list: string[]
  purchase_order_id: number | null
}

interface Invoice {
  id: number
  number: string | null
  amount: number
  total_amount: number
  commission: number
  commission_amount: number
  freight_charge: number
  deposit_due: number | null
  balance_due: number
  issue_date: string | null
  state: string
  format: string
  inland: boolean
  freight_duties: boolean
}

interface Comment {
  id: number
  body: string
  title: string | null
  user_name: string
  user_initials: string
  created_at: string
}

interface Attachment {
  id: number
  name: string
  kind: string
  url: string | null
  size: number | null
  created_at: string
}

interface SelectOption {
  id: number
  name: string
  email?: string
}

interface ShowProps {
  order: Order
  companies: SelectOption[]
  customers: SelectOption[]
  vendors: SelectOption[]
  brands: SelectOption[]
  users: SelectOption[]
  states: [string, string][]
  currencies: string[]
  seasons: [string, string][]
  probabilities: [string, string][]
  finance_stages: [string, string][]
  ship_points: [string, string][]
  /**
   * Map of CO state (symbol-as-string) → integer → display label.
   * Populated from `CustomerOrder::SUB_STATE_NAMES`. We pick the slice for the
   * currently selected state to render the Sub-Status dropdown.
   */
  sub_state_names: Record<string, Record<string, string | null>>
  sub_state_comments: Record<string, Record<string, string | null>>
  currency_symbol: string
  invoice: Invoice | null
  invoice_states: [string, string][]
  invoice_templates: [string, string][]
  customer_payment_terms: { id: number; name: string }[]
  attachment_kinds: [string, string][]
  products_count: number
  /** Eager comment count for TabNav badge — full `comments` array is deferred */
  comments_count: number
  /** Eager state-change count for TabNav log badge — `state_changes` is deferred */
  state_changes_count: number
  warehouses: { id: number; name: string }[]
  carriers: { id: number; name: string }[]
  incoterms: [string, string][]
  payment_options: { id: number; name: string }[]
  tab: string

  // ── Deferred props (Inertia 2 defer) ────────────────────────────────────
  // These arrive in a follow-up partial-reload after initial paint. Render
  // guards/skeletons in the tab switch below handle the `undefined` window.
  comments?: Comment[]
  attachments?: Attachment[]
  products?: import('./ProductsTab').Product[]
  purchase_order?: { id: number; po_number: string | null; boxes: string[] } | null
  incoming_shipment?: Record<string, unknown> | null
  outgoing_shipment?: Record<string, unknown> | null
  finance?: Record<string, unknown> | null
  summary?: Record<string, unknown> | null
  state_changes?: { id: number; changed_at: string | null; changed_at_display?: string | null; user_name: string | null; from_state: string | null; to_state: string | null; change_type: 'status' | 'sub_state' | 'finance' }[]
}

// ─── Field label map (DB name → display label) ──────────────

const FIELD_LABELS: Record<string, string> = {
  state: 'Status', probability: 'Probability', finance_stage: 'Finance Stage',
  new_season: 'Season', company_id: 'Company', customer_id: 'Customer',
  vendor_id: 'Vendor', user_id: 'Sales Rep', brand_owner: 'Brand Owner',
  units: 'Units', currency: 'Currency', ship_point: 'Ship Point',
  offer: 'Offer #', order_date: 'Order Date', vendor_po: 'Vendor PO #',
  vendor_order_date: 'Vendor Order Date', vendor_start_ship_date: 'Start Ship Date',
  vendor_cancel_date: 'Cancel Date', customer_po: 'Customer PO #',
  po_date: 'PO Date', po_start_shipping: 'Start Shipping',
  po_cancel_date: 'PO Cancel Date', customer_freight_cost: 'Inland Freight',
  customer_additional_cost: 'Additional Cost', customer_invoice_amount: 'Invoice Amount',
  amount: 'Total Amount', ghost_id: 'Ghost ID', ghost_platform_link: 'Ghost Link',
  manager_id: 'Manager', test_order: 'Test Order', tag_list: 'Categories',
  brand_ids: 'Brands', customer_payment_term_id: 'Payment Terms',
  approved: 'Approved',
}

// ─── Field components (Catalyst) ─────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <CatalystField className="grid grid-cols-[130px_1fr] items-center gap-2 py-1 [&>[data-slot=label]+[data-slot=control]]:!mt-0">
      <Label className="!text-xs/5 !font-medium !text-zinc-600 dark:!text-zinc-300">{label}</Label>
      {children}
    </CatalystField>
  )
}

function TextInput({ name, value, onChange, onSave, type = 'text', disabled }: {
  name: string; value: string | number | null; onChange: (name: string, val: string) => void; onSave: (name: string, val: string) => void; type?: string; disabled?: boolean
}) {
  return (
    <FormInput
      name={name}
      type={type as 'text'}
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
      onBlur={(e) => onSave(name, e.target.value)}
      disabled={disabled}
    />
  )
}

function SelectInput({ name, value, options, onChange, placeholder, disabled }: {
  name: string; value: string | number | null; options: { value: string; label: string }[]; onChange: (name: string, val: string) => void; placeholder?: string; disabled?: boolean
}) {
  const strValue = value != null ? String(value) : ''
  return (
    <FormSelect
      value={strValue}
      onChange={(val) => onChange(name, val)}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
    />
  )
}

function toRailsDate(isoDate: string): string {
  // Convert YYYY-MM-DD → MM/DD/YYYY for Rails params_filtering
  const [y, m, d] = isoDate.split('-')
  return y && m && d ? `${m}/${d}/${y}` : isoDate
}

// Lightweight skeleton shown while a defer'd tab payload is in-flight.
// Capybara `have_content` waits naturally pick up the real content swap.
function DeferSkeleton({ label }: { label: string }) {
  return (
    <div data-testid={`defer-skeleton-${label}`} className="flex items-center gap-2 py-8 text-sm text-zinc-400">
      <ArrowPathIcon className="size-4 animate-spin" />
      Loading {label}…
    </div>
  )
}

function DateInput({ name, value, onChange, onSave }: {
  name: string; value: string | null; onChange: (name: string, val: string) => void; onSave: (name: string, val: string) => void
}) {
  return (
    <FormInput
      name={name}
      type="date"
      value={value}
      onChange={(e) => {
        onChange(name, e.target.value)
        if (e.target.value) onSave(name, toRailsDate(e.target.value))
      }}
    />
  )
}

function MultiSelect({ values, options, onChange, allowCreate = false }: {
  values: (string | number)[]
  options: { value: string | number; label: string }[]
  onChange: (values: (string | number)[]) => void
  allowCreate?: boolean
}) {
  const [query, setQuery] = useState('')
  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const available = options.filter((o) => !values.includes(o.value))
  const filtered = query
    ? available.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : available
  const canCreate = allowCreate && query.trim().length > 0 && filtered.length === 0
  const showDropdown = focused && query.length > 0 && (filtered.length > 0 || canCreate)

  const remove = (val: string | number) => {
    onChange(values.filter((v) => v !== val))
  }

  const add = (val: string | number) => {
    const parsed = typeof val === 'string' && !isNaN(Number(val)) ? Number(val) : val
    onChange([...values, parsed])
    setQuery('')
    inputRef.current?.focus()
  }

  return (
    <div className="relative">
      <span
        data-slot="control"
        className={clsx(
          'relative block w-full',
          'before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm',
          'dark:before:hidden',
        )}
      >
        <div
          className={clsx(
            'relative flex flex-wrap items-center gap-1 rounded-lg border border-zinc-950/10 bg-transparent px-2',
            'py-[calc(--spacing(1.5)-1px)] sm:py-[calc(--spacing(1)-1px)]',
            'dark:border-white/10 dark:bg-white/5',
          )}
          onClick={() => inputRef.current?.focus()}
        >
          {values.map((val) => {
            const opt = options.find((o) => String(o.value) === String(val))
            return (
              <span key={val} className="inline-flex items-center gap-0.5 rounded bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-400">
                {opt?.label || val}
                <button type="button" onClick={(e) => { e.stopPropagation(); remove(val) }} className="hover:text-indigo-900 dark:hover:text-white">
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )
          })}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && query.trim()) {
                e.preventDefault()
                if (filtered.length > 0) {
                  add(filtered[0].value)
                } else if (allowCreate) {
                  add(query.trim())
                }
              }
              if (e.key === 'Backspace' && query === '' && values.length > 0) {
                remove(values[values.length - 1])
              }
            }}
            placeholder={values.length === 0 ? 'Type to search...' : ''}
            className="min-w-[60px] flex-1 border-0 bg-transparent p-0 text-xs/5 text-zinc-950 placeholder:text-zinc-500 focus:outline-none dark:text-white"
          />
        </div>
      </span>
      {showDropdown && (
        <div className="absolute left-0 z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-xl bg-white/75 p-1 shadow-lg ring-1 ring-zinc-950/10 backdrop-blur-xl dark:bg-zinc-800/75 dark:ring-white/10">
          {filtered.map((o) => (
            <button
              key={o.value}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => add(o.value)}
              className="block w-full rounded-lg px-2.5 py-1 text-left text-xs/5 text-zinc-950 hover:bg-indigo-500 hover:text-white dark:text-white"
            >
              {o.label}
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => add(query.trim())}
              className="block w-full rounded-lg px-2.5 py-1 text-left text-xs/5 text-indigo-600 hover:bg-indigo-500 hover:text-white dark:text-indigo-400"
            >
              Create "{query.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function InvoiceField({ label, name, value, type = 'text', invoiceId, csrfToken, onSaved, extra }: {
  label: string; name: string; value: string | number | null; type?: string; invoiceId: number; csrfToken: string; onSaved: (inv: Invoice) => void; extra?: React.ReactNode
}) {
  const [val, setVal] = useState(String(value ?? ''))
  const save = () => {
    const sendVal = type === 'date' && val ? toRailsDate(val) : val
    fetch(`/invoices/${invoiceId}.json`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ invoice: { [name]: sendVal } }),
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) onSaved(data) })
  }
  return (
    <div className="grid grid-cols-[130px_1fr] items-center gap-2 py-2">
      <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">{label}</span>
      <div className="flex items-center">
        <FormInput
          type={type as 'text'}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={save}
        />
        {extra}
      </div>
    </div>
  )
}

const sectionColors = {
  indigo:  { border: 'border-indigo-100 dark:border-indigo-500/20', bg: 'bg-indigo-50/50 dark:bg-indigo-500/5', text: '!text-indigo-600 dark:!text-indigo-400' },
  violet:  { border: 'border-violet-100 dark:border-violet-500/20', bg: 'bg-violet-50/50 dark:bg-violet-500/5', text: '!text-violet-600 dark:!text-violet-400' },
  sky:     { border: 'border-sky-100 dark:border-sky-500/20', bg: 'bg-sky-50/50 dark:bg-sky-500/5', text: '!text-sky-600 dark:!text-sky-400' },
  emerald: { border: 'border-emerald-100 dark:border-emerald-500/20', bg: 'bg-emerald-50/50 dark:bg-emerald-500/5', text: '!text-emerald-600 dark:!text-emerald-400' },
  amber:   { border: 'border-amber-100 dark:border-amber-500/20', bg: 'bg-amber-50/50 dark:bg-amber-500/5', text: '!text-amber-600 dark:!text-amber-400' },
  rose:    { border: 'border-rose-100 dark:border-rose-500/20', bg: 'bg-rose-50/50 dark:bg-rose-500/5', text: '!text-rose-600 dark:!text-rose-400' },
  zinc:    { border: 'border-zinc-100 dark:border-zinc-500/20', bg: 'bg-zinc-50/50 dark:bg-zinc-500/5', text: '!text-zinc-600 dark:!text-zinc-400' },
} as const

const sectionIcons: Record<string, { icon: React.FC<React.SVGProps<SVGSVGElement>>; fg: string; bg: string }> = {
  'Status':       { icon: ClipboardDocumentCheckIcon, fg: 'text-violet-700 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-500/10' },
  'Order Info':   { icon: ClipboardDocumentListIcon,  fg: 'text-sky-700 dark:text-sky-400',       bg: 'bg-sky-50 dark:bg-sky-500/10' },
  'Vendor PO':    { icon: DocumentTextIcon,           fg: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-500/10' },
  'Customer PO':  { icon: DocumentTextIcon,           fg: 'text-amber-700 dark:text-amber-400',   bg: 'bg-amber-50 dark:bg-amber-500/10' },
  'Financial':    { icon: BanknotesIcon,              fg: 'text-rose-700 dark:text-rose-400',     bg: 'bg-rose-50 dark:bg-rose-500/10' },
  'Advanced':     { icon: CubeIcon,                   fg: 'text-zinc-700 dark:text-zinc-400',     bg: 'bg-zinc-50 dark:bg-zinc-500/10' },
  'Invoice':      { icon: DocumentTextIcon,           fg: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-500/10' },
  'Shipping':     { icon: TruckIcon,                  fg: 'text-teal-700 dark:text-teal-400',     bg: 'bg-teal-50 dark:bg-teal-500/10' },
  'Incoming':     { icon: TruckIcon,                  fg: 'text-sky-700 dark:text-sky-400',       bg: 'bg-sky-50 dark:bg-sky-500/10' },
  'Outgoing':     { icon: TruckIcon,                  fg: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-500/10' },
  'Dates':        { icon: ListBulletIcon,             fg: 'text-indigo-700 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-500/10' },
}

// Fallback icon for sections not in the map
const fallbackIcon = { icon: ClipboardDocumentListIcon, fg: 'text-zinc-700 dark:text-zinc-400', bg: 'bg-zinc-50 dark:bg-zinc-500/10' }

function SectionCard({ title, children, color = 'indigo', actions }: { title: string; children: React.ReactNode; color?: keyof typeof sectionColors; actions?: React.ReactNode }) {
  const c = sectionColors[color]
  const iconDef = sectionIcons[title] || fallbackIcon
  const Icon = iconDef.icon

  return (
    <Fieldset className="mb-5 overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={clsx('inline-flex shrink-0 rounded-md p-1.5', iconDef.bg, iconDef.fg)}>
              <Icon className="h-4 w-4" />
            </span>
            <Legend className={clsx('!text-base !font-semibold', c.text)}>{title}</Legend>
          </div>
          {actions && <div className="flex shrink-0 gap-2">{actions}</div>}
        </div>
      </div>
      <FieldGroup className="!mt-0 !space-y-0 divide-y divide-zinc-100 border-t border-zinc-100 px-4 pt-1 pb-2 dark:divide-zinc-800 dark:border-zinc-800">
        {children}
      </FieldGroup>
    </Fieldset>
  )
}

// ─── Tab navigation ──────────────────────────────────────────

const TABS: { key: string; label: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }[] = [
  { key: 'details', label: 'Details', icon: ClipboardDocumentListSolid },
  { key: 'shipment', label: 'Shipment', icon: TruckSolid },
  { key: 'finance', label: 'Finance', icon: BanknotesSolid },
  { key: 'products', label: 'Products', icon: CubeSolid },
  { key: 'summary', label: 'Summary', icon: DocumentTextSolid },
  { key: 'log', label: 'Status Log', icon: ClockSolid },
  { key: 'comments', label: 'Comments', icon: ChatSolid },
]

function TabNav({ current, onTabChange, commentCount, productsCount, logCount }: { current: string; onTabChange: (tab: string) => void; commentCount?: number; productsCount?: number; logCount?: number }) {
  return (
    <div data-testid="tab-nav" className="mb-6 border-b border-gray-200 dark:border-zinc-700">
      <nav aria-label="Tabs" className="-mb-px flex space-x-8">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => onTabChange(tab.key)}
            aria-current={current === tab.key ? 'page' : undefined}
            className={clsx(
              'group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium',
              current === tab.key
                ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-300'
            )}
          >
            <tab.icon
              aria-hidden="true"
              className={clsx(
                'mr-2 -ml-0.5 size-5',
                current === tab.key
                  ? 'text-indigo-500 dark:text-indigo-400'
                  : 'text-gray-400 group-hover:text-gray-500 dark:text-zinc-500 dark:group-hover:text-zinc-400'
              )}
            />
            <span>{tab.label}</span>
            {tab.key === 'comments' && commentCount != null && commentCount > 0 && (
              <span className={clsx(
                'ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block',
                current === tab.key
                  ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400'
                  : 'bg-gray-100 text-gray-900 dark:bg-zinc-700 dark:text-zinc-300'
              )}>{commentCount}</span>
            )}
            {tab.key === 'products' && productsCount != null && productsCount > 0 && (
              <span className={clsx(
                'ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block',
                current === tab.key
                  ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400'
                  : 'bg-gray-100 text-gray-900 dark:bg-zinc-700 dark:text-zinc-300'
              )}>{productsCount}</span>
            )}
            {tab.key === 'log' && logCount != null && logCount > 0 && (
              <span className={clsx(
                'ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block',
                current === tab.key
                  ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400'
                  : 'bg-gray-100 text-gray-900 dark:bg-zinc-700 dark:text-zinc-300'
              )}>{logCount}</span>
            )}
          </button>
        ))}
      </nav>
    </div>
  )
}

// ─── Main ────────────────────────────────────────────────────

// ─── Main ────────────────────────────────────────────────────

export default function CustomerOrderShow({
  order, companies, customers, vendors, brands, users,
  states, currencies, seasons, probabilities, finance_stages, ship_points,
  sub_state_names, sub_state_comments,
  currency_symbol, invoice, invoice_states, invoice_templates, customer_payment_terms, comments, attachments, attachment_kinds,
  products: initialProducts, products_count, comments_count, state_changes_count, purchase_order,
  incoming_shipment, outgoing_shipment, warehouses, carriers, incoterms,
  finance, payment_options, summary, state_changes, tab,
}: ShowProps) {
  const [form, setForm] = useState({ ...order })
  const [activeTab, setActiveTabRaw] = useState(tab)
  const setActiveTab = useCallback((t: string) => {
    setActiveTabRaw(t)
    // Update URL without full reload so tab persists on browser refresh
    const url = new URL(window.location.href)
    url.searchParams.set('tab', t)
    window.history.replaceState({}, '', url.toString())
  }, [])
  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)
  // `comments` and `attachments` are deferred. Fall back to [] on initial
  // render and sync once the partial-reload chunk arrives.
  const [localComments, setLocalComments] = useState<Comment[]>(comments ?? [])
  const [localInvoice, setLocalInvoice] = useState(invoice)
  const [localAttachments, setLocalAttachments] = useState<Attachment[]>(attachments ?? [])
  const replaceProductsRef = useRef<HTMLInputElement>(null)

  useEffect(() => { if (comments) setLocalComments(comments) }, [comments])
  useEffect(() => { if (attachments) setLocalAttachments(attachments) }, [attachments])

  const set = (name: string, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const csrfToken = typeof document !== 'undefined'
    ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    : ''

  const savedValues = useRef<Record<string, string>>({})
  const saveField = useCallback(async (name: string, value: string | string[] | number[] | unknown) => {
    const strVal = String(value ?? '')
    // Skip if same as last saved value
    const lastVal = savedValues.current[name]
    if (lastVal !== undefined && lastVal === strVal) return
    try {
      const res = await fetch(`/inertia_customer_orders/${order.id}.json`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ customer_order: { [name]: value } }),
      })
      if (res.ok) {
        savedValues.current[name] = strVal
        setNotifMessage('Saved:' + (FIELD_LABELS[name] || name.replace(/_/g, ' ')))
        setShowNotif(true)
        refreshProps()
      }
    } catch { /* silent */ }
  }, [order.id, csrfToken])

  // Partial reload — refresh dependent props from server after any save.
  // Includes all defer'd props so a `saveField` cascade re-hydrates whatever
  // the user is currently looking at (state log, comments, finance, etc).
  const refreshProps = useCallback(() => {
    router.reload({
      only: [
        'invoice', 'products_count', 'comments_count', 'state_changes_count',
        'summary', 'finance', 'state_changes',
        'comments', 'attachments', 'products',
        'incoming_shipment', 'outgoing_shipment', 'purchase_order',
      ],
    })
  }, [])

  // For selects — save immediately on change
  const setAndSave = (name: string, value: string) => {
    if (String(form[name as keyof typeof form] ?? '') === value) return
    set(name, value)
    saveField(name, value)
  }

  // Invoice save — updates localInvoice from server response
  const onInvoiceSaved = (inv: Invoice) => {
    setLocalInvoice(inv)
    setNotifMessage('Saved:Invoice updated'); setShowNotif(true)
    refreshProps()
  }

  const savedInvoiceValues = useRef<Record<string, string>>({})
  const saveInvoiceField = useCallback((field: string, value: string | number | boolean) => {
    if (!localInvoice) return
    const strVal = String(value)
    if (savedInvoiceValues.current[field] === strVal) return
    fetch(`/invoices/${localInvoice.id}.json`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ invoice: { [field]: value } }),
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) { savedInvoiceValues.current[field] = strVal; onInvoiceSaved(data) } })
  }, [localInvoice?.id, csrfToken])

  const isTestOrder = form.test_order === true || form.test_order === 'true' as unknown
  const { color: stateColor, label: stateLabel } = useOrderStatus(form.state)
  const { can } = usePermissions()
  const { props: pageProps } = usePage<SharedPageProps>()
  const currentUser = pageProps.currentUser

  // Permission-gated state set: roles without `CustomerOrder:Details:status_full_set`
  // (legacy 3PL) only see the warehouse subset. Always include the order's
  // current state so a restricted user isn't locked into showing an empty
  // select on orders already past the subset.
  const WAREHOUSE_STATE_SUBSET = ['in_warehouse', 'scanned', 'partially_scanned', 'ready_to_ship'] as const
  const allowedStateKeys: string[] | null = can('CustomerOrder:Details:status_full_set')
    ? null
    : Array.from(new Set([...WAREHOUSE_STATE_SUBSET, form.state]))
  const visibleStates = allowedStateKeys
    ? states.filter(([k]) => allowedStateKeys.includes(k))
    : states

  // Advanced export formats (HTML, PDF — incl. "HTML by Box") are hidden
  // from 3PL. They still get XLSX + PDF-by-Box variants.
  const canAdvancedExport = can('CustomerOrder:Actions:export_advanced')

  // Per-option disable for the Status dropdown — ported from the legacy
  // `state_disabled?` helper. Bomi's vendor_payments fence, the stale
  // new_deal lock, parent_with_closed_gmv block, and the
  // "only adjacent timeline stages" rule are all decided here. Off-
  // timeline targets (stock, samples, consignment, etc.) stay enabled.
  const stateGuardCtx = {
    currentState: form.state,
    ghostId: order.ghost_id,
    createdAt: order.created_at,
    currentUserEmail: currentUser?.email,
  }
  // Backend hands the `states` array in pipeline-business order
  // (ApplicationHelper#sorted_order_states_options) — don't resort it.
  const stateOptions = visibleStates.map(([k, v]) => ({
    value: k,
    label: v,
    disabled: k !== form.state && isStateDisabled(k, stateGuardCtx),
  }))

  return (
    <>
      <Head title={String(order.po_number || order.id)} />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />

      {/* ── Page Header: badges left, action dropdowns right ── */}
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-zinc-900 dark:text-white">{order.po_number || order.id}</h1>
            <div className="ml-2 flex items-center gap-1.5">
              <Badge color={stateColor}>{stateLabel}</Badge>
              {form.approved && <Badge color="green">Approved</Badge>}
              {isTestOrder && <Badge color="amber">Test</Badge>}
            </div>
          </div>
          <p className="text-xs text-zinc-500">
            {order.customer_name || '—'}
            <span className="mx-2 inline-block font-semibold text-zinc-400 dark:text-zinc-600">•</span>
            {order.brand_names.join(', ') || '—'}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {/* Clubs Order Prep — Sam's Club only (mirrors the legacy haml
              `if @customer_order.customer.name == "Sam's Club"`). The
              controller redirects manual URL hits, so this is purely an
              affordance, not the security boundary. */}
          {order.customer_name === "Sam's Club" && (
            <a
              href={`/inertia_customer_orders/${order.id}/clubs_order_prep`}
              aria-label="Clubs Order Prep"
              title="Clubs Order Prep"
              data-testid="co-show-clubs-order-prep"
              className="flex items-center gap-1 rounded-lg border border-zinc-200 px-2.5 py-1.5 text-xs font-medium text-zinc-500 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-white/5"
            >
              <ShoppingCartIcon className="h-4 w-4" />
            </a>
          )}

          {/* Replace Products */}
          <Dropdown>
            <DropdownButton as="button" aria-label="Replace Products" title="Replace Products"
              className="flex items-center gap-1 rounded-lg border border-zinc-200 px-2.5 py-1.5 text-xs font-medium text-zinc-500 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-white/5">
              <ArrowPathIcon className="h-4 w-4" />
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              <CatalystDropdownItem onClick={() => replaceProductsRef.current?.click()}>
                <DropdownLabel>Replace Products</DropdownLabel>
              </CatalystDropdownItem>
            </DropdownMenu>
          </Dropdown>

          {/* Receiving Summary — its own pill-group BEFORE the export block.
              HTML + XLSX flavors of the legacy export#receiving_summary
              endpoint, gated on the existing CustomerOrder:Actions:
              receiving_summary permission. */}
          {order.purchase_order_id && can('CustomerOrder:Actions:receiving_summary') && (
            <div className="flex rounded-lg border border-zinc-200 dark:border-zinc-700">
              <Dropdown>
                <DropdownButton as="button" aria-label="Receiving Summary" title="Receiving Summary"
                  className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-white/5">
                  <ArchiveBoxIcon className="h-4 w-4" />
                </DropdownButton>
                <DropdownMenu anchor="bottom end" className="z-50">
                  <DropdownSection>
                    <DropdownHeading>Receiving Summary</DropdownHeading>
                    <CatalystDropdownItem href={`/purchase_order/${order.purchase_order_id}/export_receiving_summary`} target="_blank">
                      <DropdownLabel>HTML</DropdownLabel>
                    </CatalystDropdownItem>
                    <CatalystDropdownItem href={`/purchase_order/${order.purchase_order_id}/export_receiving_summary.xlsx`} target="_blank">
                      <DropdownLabel>XLSX</DropdownLabel>
                    </CatalystDropdownItem>
                  </DropdownSection>
                </DropdownMenu>
              </Dropdown>
            </div>
          )}

          {/* PO-dependent exports — grouped */}
          {order.purchase_order_id && (
            <div className="flex rounded-lg border border-zinc-200 dark:border-zinc-700">
              {/* Purchase Order */}
              <Dropdown>
                <DropdownButton as="button" aria-label="Purchase Order" title="Export Purchase Order"
                  className="flex items-center gap-1 rounded-l-lg px-2.5 py-1.5 text-xs font-medium text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-white/5">
                  <DocumentTextIcon className="h-4 w-4" />
                </DropdownButton>
                <DropdownMenu anchor="bottom end" className="z-50">
                  <DropdownSection>
                    <DropdownHeading>Export Purchase Order</DropdownHeading>
                    <CatalystDropdownItem href={`/purchase_orders/${order.purchase_order_id}/export_purchase_order`} target="_blank">
                      <DropdownLabel>HTML</DropdownLabel>
                    </CatalystDropdownItem>
                    <CatalystDropdownItem href={`/purchase_orders/${order.purchase_order_id}/export_purchase_order.pdf`} target="_blank">
                      <DropdownLabel>PDF</DropdownLabel>
                    </CatalystDropdownItem>
                  </DropdownSection>
                </DropdownMenu>
              </Dropdown>

              {/* Box List */}
              <Dropdown>
                <DropdownButton as="button" aria-label="Box List" title="Export Box List"
                  className="flex items-center gap-1 border-l border-zinc-200 px-2.5 py-1.5 text-xs font-medium text-zinc-500 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-white/5">
                  <CubeIcon className="h-4 w-4" />
                </DropdownButton>
                <DropdownMenu anchor="bottom end" className="z-50">
                  <DropdownSection>
                    <DropdownHeading>Export Box List</DropdownHeading>
                    {canAdvancedExport && (
                      <>
                        <CatalystDropdownItem href={`/purchase_order/${order.purchase_order_id}/export_box_list`} target="_blank">
                          <DropdownLabel>HTML</DropdownLabel>
                        </CatalystDropdownItem>
                        <CatalystDropdownItem href={`/purchase_order/${order.purchase_order_id}/export_box_list.pdf`} target="_blank">
                          <DropdownLabel>PDF</DropdownLabel>
                        </CatalystDropdownItem>
                      </>
                    )}
                    <CatalystDropdownItem href={`/purchase_order/${order.purchase_order_id}/export_box_list.xlsx`} target="_blank">
                      <DropdownLabel>XLSX</DropdownLabel>
                    </CatalystDropdownItem>
                  </DropdownSection>
                </DropdownMenu>
              </Dropdown>

              {/* Packing List */}
              <Dropdown>
                <DropdownButton as="button" aria-label="Packing List" title="Export Packing List"
                  className="flex items-center gap-1 border-l border-zinc-200 px-2.5 py-1.5 text-xs font-medium text-zinc-500 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-white/5">
                  <ClipboardDocumentListIcon className="h-4 w-4" />
                </DropdownButton>
                <DropdownMenu anchor="bottom end" className="z-50">
                  <DropdownSection>
                    <DropdownHeading>Export Packing List</DropdownHeading>
                    {canAdvancedExport && (
                      <>
                        <CatalystDropdownItem href={`/purchase_order/${order.purchase_order_id}/export_packing_list`} target="_blank">
                          <DropdownLabel>HTML</DropdownLabel>
                        </CatalystDropdownItem>
                        <CatalystDropdownItem href={`/purchase_order/${order.purchase_order_id}/export_packing_list.pdf`} target="_blank">
                          <DropdownLabel>PDF</DropdownLabel>
                        </CatalystDropdownItem>
                        <CatalystDropdownItem href={`/purchase_order/${order.purchase_order_id}/export_packing_list.xlsx`} target="_blank">
                          <DropdownLabel>XLSX</DropdownLabel>
                        </CatalystDropdownItem>
                        <DropdownDivider />
                      </>
                    )}
                    <CatalystDropdownItem href={`/purchase_order/${order.purchase_order_id}/export_each_box_packing_lists.pdf`} target="_blank">
                      <DropdownLabel>PDF by Box</DropdownLabel>
                    </CatalystDropdownItem>
                    <CatalystDropdownItem href={`/purchase_order/${order.purchase_order_id}/export_each_box_packing_lists.pdf?empty=true`} target="_blank">
                      <DropdownLabel>PDF by Box — Empty</DropdownLabel>
                    </CatalystDropdownItem>
                    <CatalystDropdownItem href={`/purchase_order/${order.purchase_order_id}/export_each_box_packing_lists.pdf?no_affari=true`} target="_blank">
                      <DropdownLabel>PDF by Box — No Affari</DropdownLabel>
                    </CatalystDropdownItem>
                    {canAdvancedExport && (
                      <>
                        <DropdownDivider />
                        <CatalystDropdownItem href={`/purchase_order/${order.purchase_order_id}/export_each_box_packing_lists`} target="_blank">
                          <DropdownLabel>HTML by Box</DropdownLabel>
                        </CatalystDropdownItem>
                      </>
                    )}
                  </DropdownSection>
                </DropdownMenu>
              </Dropdown>

              {/* Product List */}
              <Dropdown>
                <DropdownButton as="button" aria-label="Product List" title="Export Product List"
                  className="flex items-center gap-1 rounded-r-lg border-l border-zinc-200 px-2.5 py-1.5 text-xs font-medium text-zinc-500 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-white/5">
                  <ListBulletIcon className="h-4 w-4" />
                </DropdownButton>
                <DropdownMenu anchor="bottom end" className="z-50">
                  <DropdownSection>
                    <DropdownHeading>Export Product List</DropdownHeading>
                    <CatalystDropdownItem href={`/purchase_orders/${order.purchase_order_id}/export_product_list.xlsx`} target="_blank">
                      <DropdownLabel>XLSX</DropdownLabel>
                    </CatalystDropdownItem>
                  </DropdownSection>
                </DropdownMenu>
              </Dropdown>
            </div>
          )}

          {/* Clone — separate pill-group AFTER exports. Native POST form
              opens in a new tab; legacy used the same target="_blank"
              behavior so the user lands on the cloned order without
              losing their place on the original. Gated by the new
              CustomerOrder:clone permission. */}
          {can('CustomerOrder:clone') && (
            <form action={`/customer_orders/${order.id}/clone`} method="POST" target="_blank" className="inline-flex">
              <input type="hidden" name="authenticity_token" value={csrfToken} />
              <button
                type="submit"
                aria-label="Clone"
                title="Clone this order"
                className="flex items-center justify-center rounded-lg border border-zinc-200 px-2.5 py-1.5 text-xs font-medium text-zinc-500 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-white/5"
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Hidden file input for Replace Products */}
      <input
        ref={replaceProductsRef}
        type="file"
        className="hidden"
        accept=".xlsx,.xls,.csv"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (!file) return
          if (!confirm('Are you sure you want to replace products list?')) { e.target.value = ''; return }
          const formData = new FormData()
          formData.append('products[file]', file)
          fetch(`/inertia_customer_orders/${order.id}/replace_products.json`, {
            method: 'POST',
            headers: { 'X-CSRF-Token': csrfToken },
            body: formData,
          }).then((res) => {
            if (res.ok) {
              setNotifMessage('Saved:Products replaced'); setShowNotif(true)
              router.reload()
            } else {
              setNotifMessage('error:Error:Failed to replace products'); setShowNotif(true)
            }
          })
          e.target.value = ''
        }}
      />

      <TabNav current={activeTab} onTabChange={setActiveTab} commentCount={comments_count} productsCount={products_count} logCount={state_changes_count} />

      {activeTab === 'details' && <div data-testid="details-form" className="grid grid-cols-1 gap-x-8 lg:grid-cols-2">
        {/* ── Left column ── */}
        <div>
          <SectionCard title="Status" color="violet">
            <Field label="Status">
              <div data-testid="state-select">
                <SelectInput name="state" value={form.state} onChange={setAndSave} options={stateOptions} />
              </div>
            </Field>
            {/*
              Sub-status options are state-dependent: SUB_STATE_NAMES is keyed
              by CO state. Always render both fields — matching legacy HAML
              UX — but disable the select when the current state has no
              options, so the user sees "the field exists for some states,
              just not this one" instead of wondering where it went.
            */}
            {(() => {
              const namesForState      = sub_state_names?.[form.state] || {}
              const commentsForState   = sub_state_comments?.[form.state] || {}
              const nameOptions        = Object.entries(namesForState).map(([k, v]) => ({ value: k, label: v || '—' }))
              const commentOptions     = Object.entries(commentsForState).map(([k, v]) => ({ value: k, label: v || '—' }))
              const nameDisabled       = nameOptions.length === 0
              const commentDisabled    = commentOptions.length === 0
              // When the current state has no sub-status options we still
              // render the field — but with a single greyed-out placeholder
              // and the listbox disabled, so the user can see the field
              // exists for some states without being able to interact.
              const namePlaceholder    = nameDisabled    ? [{ value: '0', label: 'No sub-status for this state' }]         : nameOptions
              const commentPlaceholder = commentDisabled ? [{ value: '0', label: 'No sub-status comment for this state' }] : commentOptions
              return (
                <>
                  <div data-testid="sub-state-name-field">
                    <Field label="Sub Status">
                      <SelectInput
                        name="sub_state_name"
                        value={nameDisabled ? '0' : String(form.sub_state_name ?? 0)}
                        onChange={setAndSave}
                        options={namePlaceholder}
                        disabled={nameDisabled}
                      />
                    </Field>
                  </div>
                  <div data-testid="sub-state-comment-field">
                    <Field label="Sub Status Comment">
                      <SelectInput
                        name="sub_state_comment"
                        value={commentDisabled ? '0' : String(form.sub_state_comment ?? 0)}
                        onChange={setAndSave}
                        options={commentPlaceholder}
                        disabled={commentDisabled}
                      />
                    </Field>
                  </div>
                </>
              )
            })()}
            <Field label="Probability">
              <SelectInput name="probability" value={form.probability} onChange={setAndSave}
                options={probabilities.map(([k, v]) => ({ value: k, label: v }))} />
            </Field>
            <Field label="Finance Stage">
              <SelectInput name="finance_stage" value={form.finance_stage} onChange={setAndSave}
                options={finance_stages.map(([k, v]) => ({ value: k, label: v }))} />
            </Field>
            <Field label="Season">
              <SelectInput name="new_season" value={form.new_season} onChange={setAndSave}
                options={seasons.map(([k, v]) => ({ value: k, label: v }))} />
            </Field>
          </SectionCard>

          <SectionCard title="Order Info" color="sky">
            <Field label="Company">
              <SelectInput name="company_id" value={form.company_id} onChange={setAndSave} placeholder="Select..."
                options={companies.map((c) => ({ value: String(c.id), label: c.name }))} />
            </Field>
            <Field label="Categories">
              <MultiSelect
                values={form.tag_list}
                options={form.tag_list.map((t) => ({ value: t, label: t }))}
                allowCreate
                onChange={(vals) => {
                  set('tag_list', vals as unknown as string)
                  saveField('tag_list', vals as unknown as string)
                }}
              />
            </Field>
            <Field label="Brands">
              <MultiSelect
                values={form.brand_ids}
                options={brands.map((b) => ({ value: b.id, label: b.name }))}
                onChange={(vals) => {
                  set('brand_ids', vals as unknown as string)
                  saveField('brand_ids', vals as unknown as string)
                }}
              />
            </Field>
            <Field label="Customer">
              <SelectInput name="customer_id" value={form.customer_id} onChange={setAndSave} placeholder="Select..."
                options={customers.map((c) => ({ value: String(c.id), label: c.name }))} />
            </Field>
            <Field label="Vendor">
              <SelectInput name="vendor_id" value={form.vendor_id} onChange={setAndSave} placeholder="Select..."
                options={vendors.map((v) => ({ value: String(v.id), label: v.name }))} />
            </Field>
            <Field label="Sales Rep">
              <SelectInput name="user_id" value={form.user_id} onChange={setAndSave} placeholder="Select..."
                options={users.map((u) => ({ value: String(u.id), label: u.name || u.email || '' }))} />
            </Field>
            <Field label="Brand Owner">
              <TextInput name="brand_owner" value={form.brand_owner} onChange={set} onSave={saveField} />
            </Field>
            <Field label="Units">
              <TextInput name="units" value={form.units} onChange={set} onSave={saveField} type="number" />
            </Field>
            <Field label="Currency">
              <SelectInput name="currency" value={form.currency} onChange={setAndSave}
                options={currencies.map((c) => ({ value: c, label: c.toUpperCase() }))} />
            </Field>
            <Field label="Ship Point">
              <SelectInput name="ship_point" value={form.ship_point} onChange={setAndSave}
                options={ship_points.map(([k, v]) => ({ value: k, label: v }))} />
            </Field>
            <Field label="Offer #">
              <TextInput name="offer" value={form.offer} onChange={set} onSave={saveField} />
            </Field>
            <Field label="Order Date">
              <DateInput name="order_date" value={form.order_date} onChange={set} onSave={saveField} />
            </Field>
          </SectionCard>

          <SectionCard title="Vendor PO" color="emerald">
            <Field label="Vendor PO #">
              <TextInput name="vendor_po" value={form.vendor_po} onChange={set} onSave={saveField} />
            </Field>
            <Field label="Vendor Order Date">
              <DateInput name="vendor_order_date" value={form.vendor_order_date} onChange={set} onSave={saveField} />
            </Field>
            <Field label="Start Ship Date">
              <DateInput name="vendor_start_ship_date" value={form.vendor_start_ship_date} onChange={set} onSave={saveField} />
            </Field>
            <Field label="Cancel Date">
              <DateInput name="vendor_cancel_date" value={form.vendor_cancel_date} onChange={set} onSave={saveField} />
            </Field>
          </SectionCard>

          <SectionCard title="Customer PO" color="amber">
            <Field label="Customer PO #">
              <TextInput name="customer_po" value={form.customer_po} onChange={set} onSave={saveField} />
            </Field>
            <Field label="PO Date">
              <DateInput name="po_date" value={form.po_date} onChange={set} onSave={saveField} />
            </Field>
            <Field label="Start Ship Date">
              <DateInput name="po_start_shipping" value={form.po_start_shipping} onChange={set} onSave={saveField} />
            </Field>
            <Field label="Cancel Date">
              <DateInput name="po_cancel_date" value={form.po_cancel_date} onChange={set} onSave={saveField} />
            </Field>
          </SectionCard>
        </div>

        {/* ── Right column: Invoice → Attachments → Financial → Advanced ── */}
        <div>
          {/* ═══ INVOICE ═══ */}
          <div className="mb-5 overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10" data-testid="invoice-section">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="inline-flex shrink-0 rounded-lg bg-cyan-50 p-2 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-400">
                    <DocumentTextIcon className="h-4 w-4" />
                  </span>
                  <h3 className="text-base font-semibold text-cyan-600 dark:text-cyan-400">
                    Invoice {localInvoice?.format && <span className="text-sm font-normal text-zinc-500">({localInvoice.format.replace(/_/g, ' ')})</span>}
                  </h3>
                </div>
                {localInvoice && (
                  <span className="text-sm font-bold text-zinc-900 dark:text-white">
                    {currency_symbol}{localInvoice.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                )}
              </div>
            </div>
            <div className="border-t border-zinc-100 p-4 dark:border-zinc-800">
              {localInvoice ? (
                <div>
                  {/* Two-column grid for fields */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0 divide-y divide-zinc-100 dark:divide-zinc-800 [&>*]:col-span-2 sm:[&>*]:col-span-1">
                    <InvoiceField label="Invoice #" name="number" value={localInvoice.number} invoiceId={localInvoice.id} csrfToken={csrfToken} onSaved={onInvoiceSaved} />
                    <InvoiceField label="Commission (%)" name="commission" value={localInvoice.commission} type="number" invoiceId={localInvoice.id} csrfToken={csrfToken} onSaved={onInvoiceSaved}
                      extra={<span className="ml-1 text-xs text-zinc-400">({currency_symbol}{localInvoice.commission_amount.toFixed(2)})</span>} />
                    <InvoiceField label="Freight Charge" name="freight_charge" value={localInvoice.freight_charge} type="number" invoiceId={localInvoice.id} csrfToken={csrfToken} onSaved={onInvoiceSaved} />
                    <div className="grid grid-cols-[130px_1fr] items-center gap-2 py-2">
                      <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Inland</span>
                      <FormCheckbox checked={localInvoice.inland ?? false} onChange={(val) => saveInvoiceField('inland', val)} />
                    </div>
                    <div className="grid grid-cols-[130px_1fr] items-center gap-2 py-2">
                      <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Freight/Duties</span>
                      <FormCheckbox checked={localInvoice.freight_duties ?? false} onChange={(val) => saveInvoiceField('freight_duties', val)} />
                    </div>
                    <InvoiceField label="Deposit Due (%)" name="deposit_due" value={localInvoice.deposit_due ?? 0} type="number" invoiceId={localInvoice.id} csrfToken={csrfToken} onSaved={onInvoiceSaved} />
                    <div className="grid grid-cols-[130px_1fr] items-center gap-2 py-2">
                      <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Payment Terms</span>
                      <FormSelect
                        value={String(order.customer_payment_term_id || '')}
                        onChange={(val) => saveField('customer_payment_term_id', val)}
                        placeholder="—"
                        options={customer_payment_terms.map((t) => ({ value: String(t.id), label: t.name }))}
                      />
                    </div>
                    <InvoiceField label="Balance Due (%)" name="balance_due" value={localInvoice.balance_due} type="number" invoiceId={localInvoice.id} csrfToken={csrfToken} onSaved={onInvoiceSaved} />
                    <InvoiceField label="Issued On" name="issue_date" value={localInvoice.issue_date} type="date" invoiceId={localInvoice.id} csrfToken={csrfToken} onSaved={onInvoiceSaved} />
                    <div className="grid grid-cols-[130px_1fr] items-center gap-2 py-2">
                      <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Status</span>
                      <FormSelect
                        value={localInvoice.state}
                        onChange={(val) => saveInvoiceField('state', val)}
                        options={invoice_states.map(([k, v]) => ({ value: k, label: v }))}
                      />
                    </div>
                  </div>

                  {/* Export + Delete buttons */}
                  <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-zinc-100 pt-3 dark:border-zinc-800">
                    <Dropdown>
                      <DropdownButton as="button" className="rounded-md border border-zinc-200 px-2.5 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-50 dark:border-zinc-600 dark:text-zinc-400">
                        Export Invoice
                      </DropdownButton>
                      <DropdownMenu anchor="bottom start" className="z-50">
                        {(invoice_templates || []).map(([name, slug]) => (
                          <CatalystDropdownItem key={slug} href={`/invoices/${localInvoice.id}.pdf?template=${slug}`} target="_blank">
                            <DropdownLabel>{name}</DropdownLabel>
                          </CatalystDropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                    <Dropdown>
                      <DropdownButton as="button" className="rounded-md border border-zinc-200 px-2.5 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-50 dark:border-zinc-600 dark:text-zinc-400">
                        Export Proforma
                      </DropdownButton>
                      <DropdownMenu anchor="bottom start" className="z-50">
                        {(invoice_templates || []).map(([name, slug]) => (
                          <CatalystDropdownItem key={slug} href={`/invoices/${localInvoice.id}.pdf?template=${slug}&proforma=true`} target="_blank">
                            <DropdownLabel>{name}</DropdownLabel>
                          </CatalystDropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm('Delete this invoice?')) {
                          fetch(`/invoices/${localInvoice.id}.json`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } })
                            .then((res) => {
                              if (res.ok) {
                                setLocalInvoice(null)
                                setNotifMessage('Deleted:Invoice removed'); setShowNotif(true); refreshProps()
                              }
                            })
                        }
                      }}
                      className="rounded-md border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:text-red-400"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-center">
                  <p className="text-xs text-zinc-500">No invoice created yet</p>
                  <div className="flex justify-center gap-2">
                    <button type="button" onClick={() => {
                      fetch(`/customer_orders/${order.id}/invoices.json`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }, body: JSON.stringify({ invoice_format: 'from_order' }) })
                        .then(res => res.ok ? res.json() : Promise.reject())
                        .then((data) => { setLocalInvoice(data); setNotifMessage('Saved:Invoice created'); setShowNotif(true); refreshProps() })
                        .catch(() => { setNotifMessage('error:Error:Failed to create invoice'); setShowNotif(true) })
                    }} className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500">
                      From Order
                    </button>
                    <button type="button" onClick={() => {
                      fetch(`/customer_orders/${order.id}/invoices.json`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }, body: JSON.stringify({ invoice_format: 'from_receiving' }) })
                        .then(res => res.ok ? res.json() : Promise.reject())
                        .then((data) => { setLocalInvoice(data); setNotifMessage('Saved:Invoice created'); setShowNotif(true); refreshProps() })
                        .catch(() => { setNotifMessage('error:Error:Failed to create invoice'); setShowNotif(true) })
                    }} className="rounded-md border border-zinc-300 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-600 dark:text-zinc-300 dark:hover:bg-zinc-800">
                      From Receiving
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ═══ ATTACHMENTS ═══ */}
          <div className="mb-5 overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
            <div className="p-4">
              <div className="flex items-center gap-3">
                <span className="inline-flex shrink-0 rounded-lg bg-orange-50 p-2 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400">
                  <CubeIcon className="h-4 w-4" />
                </span>
                <h3 className="text-base font-semibold text-orange-600 dark:text-orange-400">
                  Attached Files {localAttachments.length > 0 && <span className="text-sm font-normal text-zinc-500">({localAttachments.length})</span>}
                </h3>
              </div>
            </div>
            <div className="border-t border-zinc-100 p-4 dark:border-zinc-800">
              {localAttachments.length > 0 ? (
                <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
                  {localAttachments.map((att) => (
                    <div key={att.id} className="flex items-center justify-between gap-2 py-2 text-xs">
                      {/*
                        min-w-0 on the flex child is what actually lets the
                        inner truncate take effect — without it the child
                        defaults to min-content and overflows the row.
                        block + truncate gives single-line ellipsis when the
                        filename is too long for the available width.
                      */}
                      <div className="flex min-w-0 flex-1 items-baseline gap-2">
                        {att.url ? (
                          <a href={att.url} target="_blank" rel="noreferrer" title={att.name}
                             className="block min-w-0 flex-1 truncate font-medium text-indigo-600 hover:underline dark:text-indigo-400">{att.name}</a>
                        ) : (
                          <span title={att.name}
                                className="block min-w-0 flex-1 truncate font-medium text-zinc-700 dark:text-zinc-300">{att.name}</span>
                        )}
                        <span className="shrink-0 whitespace-nowrap text-zinc-400">{att.kind.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="flex shrink-0 items-center gap-2 whitespace-nowrap text-zinc-400">
                        <span>{att.created_at}</span>
                        <button type="button" onClick={() => {
                          if (confirm('Delete this file?')) {
                            fetch(`/customer_orders/${order.id}/attachments/${att.id}.json`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } })
                              .then((res) => {
                                if (res.ok) {
                                  setLocalAttachments(prev => prev.filter(a => a.id !== att.id))
                                  setNotifMessage('Deleted:Attachment removed'); setShowNotif(true)
                                }
                              })
                          }
                        }} className="text-zinc-400 hover:text-red-500">×</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-zinc-400">No files attached</p>
              )}
              <div className="mt-3 border-t border-zinc-100 pt-3 dark:border-zinc-800">
                <FileUpload kindOptions={attachment_kinds} onUpload={(formData) => {
                  fetch(`/customer_orders/${order.id}/attachments.json`, { method: 'POST', headers: { 'X-CSRF-Token': csrfToken }, body: formData })
                    .then((res) => {
                      if (res.ok) return res.json()
                      throw new Error('Upload failed')
                    })
                    .then((data) => {
                      setLocalAttachments(data)
                      setNotifMessage('Saved:File uploaded'); setShowNotif(true)
                    })
                    .catch(() => { setNotifMessage('error:Error:Upload failed'); setShowNotif(true) })
                }} />
              </div>
            </div>
          </div>

          {/* ═══ FINANCIAL ═══ */}
          <SectionCard title="Financial" color="rose">
            <Field label={`Inland Freight (${currency_symbol})`}>
              <TextInput name="customer_freight_cost" value={form.customer_freight_cost} onChange={set} onSave={saveField} type="number" />
            </Field>
            <Field label={`Additional Cost (${currency_symbol})`}>
              <TextInput name="customer_additional_cost" value={form.customer_additional_cost} onChange={set} onSave={saveField} type="number" />
            </Field>
            <Field label={`Invoice Amount (${currency_symbol})`}>
              <TextInput name="customer_invoice_amount" value={form.customer_invoice_amount} onChange={set} onSave={saveField} type="number" disabled />
            </Field>
            <Field label={`Total Amount (${currency_symbol})`}>
              <TextInput name="amount" value={form.amount} onChange={set} onSave={saveField} type="number" disabled />
            </Field>
          </SectionCard>

          {/* ═══ ADVANCED ═══ */}
          <SectionCard title="Advanced" color="zinc">
            <Field label="Ghost ID">
              <TextInput name="ghost_id" value={form.ghost_id} onChange={set} onSave={saveField} type="number" />
            </Field>
            <Field label="Ghost Link">
              <TextInput name="ghost_platform_link" value={form.ghost_platform_link} onChange={set} onSave={saveField} />
            </Field>
            <Field label="Manager">
              <SelectInput name="manager_id" value={form.manager_id} onChange={setAndSave} placeholder="Select..."
                options={users.map((u) => ({ value: String(u.id), label: u.name || u.email || '' }))} />
            </Field>
            <Field label="Test Order">
              <SelectInput name="test_order" value={form.test_order ? 'true' : 'false'} onChange={setAndSave}
                options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]} />
            </Field>
          </SectionCard>

        </div>
      </div>}

      {/* ═══ SUMMARY TAB ═══ */}
      {activeTab === 'summary' && (summary ? (
        <SummaryTab
          summary={summary as unknown as Parameters<typeof SummaryTab>[0]['summary']}
          currencySymbol={currency_symbol}
          orderId={order.id}
          csrfToken={csrfToken}
          onNotify={(msg) => { setNotifMessage(msg); setShowNotif(true); refreshProps() }}
          onSaveField={saveField}
        />
      ) : <DeferSkeleton label="summary" />)}

      {/* ═══ STATUS LOG TAB ═══ */}
      {activeTab === 'log' && (state_changes ? (
        <StatusLogTab stateChanges={state_changes} />
      ) : <DeferSkeleton label="status log" />)}

      {/* ═══ FINANCE TAB ═══ */}
      {activeTab === 'finance' && (finance ? (
        <FinanceTab
          finance={finance as unknown as Parameters<typeof FinanceTab>[0]['finance']}
          orderId={order.id}
          currencySymbol={currency_symbol}
          csrfToken={csrfToken}
          paymentOptions={payment_options}
          onNotify={(msg) => { setNotifMessage(msg); setShowNotif(true); refreshProps() }}
          onSaveField={saveField}
        />
      ) : <DeferSkeleton label="finance" />)}

      {/* ═══ SHIPMENT TAB ═══ */}
      {activeTab === 'shipment' && (incoming_shipment !== undefined || outgoing_shipment !== undefined ? (
        <ShipmentTab
          incomingShipment={(incoming_shipment ?? null) as Parameters<typeof ShipmentTab>[0]['incomingShipment']}
          outgoingShipment={(outgoing_shipment ?? null) as Parameters<typeof ShipmentTab>[0]['outgoingShipment']}
          warehouses={warehouses}
          carriers={carriers}
          incoterms={incoterms}
          csrfToken={csrfToken}
          currencySymbol={currency_symbol}
          onNotify={(msg) => { setNotifMessage(msg); setShowNotif(true); refreshProps() }}
        />
      ) : <DeferSkeleton label="shipment" />)}

      {/* ═══ PRODUCTS TAB ═══ */}
      {activeTab === 'products' && (initialProducts ? (
        <ProductsTab
          products={initialProducts}
          orderId={order.id}
          purchaseOrder={purchase_order ?? null}
          currencySymbol={currency_symbol}
          csrfToken={csrfToken}
          onNotify={(msg) => { setNotifMessage(msg); setShowNotif(true); refreshProps() }}
        />
      ) : <DeferSkeleton label="products" />)}

      {/* ═══ COMMENTS TAB ═══ */}
      {/*
        `comments` is deferred. Once `comments` !== undefined the list is
        authoritative (could legitimately be empty). Before that we show a
        skeleton instead of "No comments yet" — otherwise an order with N
        existing comments flashes empty until the defer round-trip lands.
      */}
      {activeTab === 'comments' && comments === undefined && (
        <DeferSkeleton label="comments" />
      )}
      {activeTab === 'comments' && comments !== undefined && (
        <div data-testid="comments-tab">
          {/* ── Chat-style message list ── */}
          <div className="mb-4 space-y-3">
            {localComments.length === 0 && (
              <p className="text-sm text-zinc-400">No comments yet</p>
            )}
            {localComments.map((c) => (
              <div key={c.id} className="group flex gap-3" data-testid="comment-message">
                {/* Avatar */}
                <Avatar name={c.user_name} size="sm" />
                {/* Bubble */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs font-semibold text-zinc-900 dark:text-white">{c.user_name}</span>
                    <span className="text-[10px] text-zinc-400">{c.created_at}</span>
                  </div>
                  {c.title && <p className="text-xs font-medium text-zinc-500">{c.title}</p>}
                  <div className="mt-0.5 flex items-start gap-2">
                    <div className="flex-1 rounded-lg rounded-tl-none bg-zinc-50 px-3 py-2 text-sm text-zinc-800 dark:bg-zinc-800 dark:text-zinc-200">
                      {c.body}
                    </div>
                    {can('Comment:destroy') && (
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm('Delete this comment?')) {
                            fetch(`/customer_orders/${order.id}/comments/${c.id}.json`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } })
                              .then((res) => {
                                if (res.ok) {
                                  setLocalComments(prev => prev.filter(x => x.id !== c.id))
                                  setNotifMessage('Deleted:Comment removed'); setShowNotif(true)
                                }
                              })
                          }
                        }}
                        className="mt-1.5 shrink-0 text-zinc-300 hover:text-red-500 dark:text-zinc-600 dark:hover:text-red-400"
                        title="Delete comment"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Compose form ── */}
          {can('CustomerOrder:comments') && <form
            className="flex items-end gap-2"
            onSubmit={async (e) => {
              e.preventDefault()
              const textarea = e.currentTarget.elements.namedItem('body') as HTMLTextAreaElement
              const body = textarea.value.trim()
              if (!body) return
              const res = await fetch(`/customer_orders/${order.id}/comments.json`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
                body: JSON.stringify({ comment: { body } }),
              })
              if (res.ok) {
                const data = await res.json()
                setLocalComments(prev => [data, ...prev])
                textarea.value = ''
                setNotifMessage('Saved:Comment posted'); setShowNotif(true)
              }
            }}
          >
            <textarea
              name="body"
              placeholder="Write a comment..."
              rows={2}
              className="flex-1 resize-y rounded-lg border border-zinc-950/10 bg-transparent px-3 py-2 text-sm text-zinc-950 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-white"
            />
            <button type="submit" className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500">
              Post
            </button>
          </form>}
        </div>
      )}
    </>
  )
}

CustomerOrderShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
